/**
 * Chat Image Hooks
 *
 * TanStack Query hooks for chat image conversations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  createConversation,
  deleteConversation,
  getConversation,
  listConversations,
  renameConversation,
  saveImageToGallery,
  sendMessage,
  uploadReferenceImage,
} from '@/lib/api/chat-image';

const KEYS = {
  conversations: ['chat-image', 'conversations'] as const,
  conversation: (id: string) => ['chat-image', 'conversation', id] as const,
};

export function useChatConversations() {
  return useQuery({
    queryKey: KEYS.conversations,
    queryFn: () => listConversations({ limit: 50 }),
  });
}

export function useChatConversation(conversationId: string | null) {
  return useQuery({
    queryKey: KEYS.conversation(conversationId ?? ''),
    queryFn: () => getConversation(conversationId!),
    enabled: !!conversationId,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.conversations });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.conversations });
    },
  });
}

export function useSendChatMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { text: string; image_ids?: string[]; locale?: string; aspect_ratio?: string; resolution?: string }) =>
      sendMessage(conversationId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.conversation(conversationId) });
      qc.invalidateQueries({ queryKey: KEYS.conversations });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate image');
    },
  });
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { conversationId: string; title: string }) =>
      renameConversation(params.conversationId, params.title),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.conversations });
      qc.invalidateQueries({ queryKey: KEYS.conversation(vars.conversationId) });
    },
  });
}

export function useSaveToGallery() {
  return useMutation({
    mutationFn: (params: { imageId: string; conversationId: string; messageId: string }) =>
      saveImageToGallery(params.imageId, {
        conversation_id: params.conversationId,
        message_id: params.messageId,
      }),
    onSuccess: () => {
      toast.success('Image saved to gallery');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save to gallery');
    },
  });
}

export function useUploadReferenceImage(conversationId: string) {
  return useMutation({
    mutationFn: (file: File) => uploadReferenceImage(conversationId, file),
    onError: (error: Error) => {
      toast.error(error.message || 'Upload failed');
    },
  });
}
